// src/app/argent/useFacturesPieces.js — les pièces : transmise, brouillon émis
// (garde TVA, estSerieConciergerie), numérotation, aperçu, suppression, avoirs.
// CHEMIN ARGENT. Déplacé octet pour octet depuis src/App.jsx (P-3.5, squelette,
// partie 2) : seuls changent l'emballage (signature, retour) et les imports.

import { supabase } from '../../supabase'
import { useEffect } from 'react'
import { TEXTE_FACTURE_LECTURE_RATEE } from '../../lib/argent-lecture'
import { deleteFactureCoherent as deleteFactureFlow } from '../../lib/invoice-delete-flow'
import { genFactureHTML as genFactureHTMLPure, inflateFactureSnapshot } from '../../lib/facture-html'
import { ouvrirApercuIsole } from '../../lib/apercu-isole'
import { PREFIX_COMMISSION, PREFIX_VOYAGEUR_FBZ, resolveInvoicePrefix as resolveInvoicePrefixV2, isDraftInvoice } from '../../lib/invoices'
import { emitAvoirCoherent as emitAvoirFlow } from '../../lib/invoice-avoir-flow'
// P-2.46 : garde « Assujetti TVA » (une seule porte, partagée avec le serveur).
import { refusEmissionTva } from '../../lib/tva-garde'
import { sendAvoirCoherent as sendAvoirFlow } from '../../lib/avoir-send-flow'
import { genAvoirHTML } from '../../lib/avoir-html'
import { ensureEmailJs, EMAILJS_SERVICE } from '../../lib/emailjs-loader'
import { loadSnapshotForEmail } from '../../lib/facture-snapshot'
import { computePreviewNumber } from '../../lib/invoice-preview'

// P-2.58 (30/10, lot A1) — « Prochain numéro » RELU après chaque émission.
// Le crochet commun : toute émission faite dans l'appli annonce
// `fbz:numero-emis` ; la section « Compteurs de factures » des Réglages
// l'écoute et relit les compteurs en base (elle relit aussi au retour sur
// l'onglet, pour les pièces émises par le départ automatique). Jamais un
// numéro calculé de mémoire.
export const EVENEMENT_NUMERO_EMIS = 'fbz:numero-emis'
export function signalerNumeroEmis() {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return
  window.dispatchEvent(new Event(EVENEMENT_NUMERO_EMIS))
}

export function useFacturesPieces({
  cfg, argentLectureRatee, data, invoicesList, invoicesArchiveList, setConfirmAvoirChecked, setConfirmEnvoiAvoirChecked, incidents, month, tab, setConfirm,
  currentUser, y, m, monthLabel, setInvoiceSequences, invoiceSequences, factureOpLock, saveLog,
  callDeleteInvoice, refreshInvoices, callSetInvoiceDelivered,
}) {
  // « Émettre » un brouillon (2026-09-30) — remplace la pastille « Non envoyé »
  // qui, cliquée, posait la date de transmission à la main et attribuait le
  // numéro sans le dire. Le geste est confirmé dans la modale
  // `emettreBrouillon` ; ICI, il attribue le numéro et RIEN d'autre : aucun
  // courriel, aucune date de transmission (option serveur emettreSeulement).
  // P-2.46 : séries de la conciergerie (FBZ SAS) — commission P, voyageur V.
  const estSerieConciergerie = (f) => !!f && (f.prefix === PREFIX_COMMISSION || f.prefix === PREFIX_VOYAGEUR_FBZ)
  const emettreBrouillon = async (f) => {
    // F13 : aucune facture numérotée pendant une lecture ratée.
    if (argentLectureRatee) { alert(TEXTE_FACTURE_LECTURE_RATEE); setConfirm(null); return }
    // P-2.46 : série de la conciergerie (P, V) et « Assujetti TVA » coché ⇒
    // refus avant l'appel. Toute autre série : le serveur tranche (même garde).
    const refusTvaBrouillon = estSerieConciergerie(f) && refusEmissionTva(cfg, { emetteur: 'fbz' })
    if (refusTvaBrouillon) { alert('⛔ ' + refusTvaBrouillon.message); setConfirm(null); return }
    if (factureOpLock.current) return
    factureOpLock.current = true
    try {
      const r = await callSetInvoiceDelivered(f.invoiceId, true, { emettreSeulement: true })
      if (!r.ok) { alert(r.message); return }
      alert(`✅ Facture émise : ${r.invoice_number}. Aucun courriel n'est parti — elle apparaît « Émise, non envoyée ».`)
      signalerNumeroEmis()
      callGetInvoiceSequences().then(seqs => setInvoiceSequences(seqs || [])).catch(e => console.warn('[emettreBrouillon/sequences]', e?.message))
    } catch (e) {
      console.error('[emettreBrouillon]', e)
      alert("Erreur lors de l'émission : " + (e?.message || String(e)))
    } finally {
      factureOpLock.current = false
      setConfirm(null)
      refreshInvoices()
    }
  }
  // Charge les factures (table invoices) à l'ouverture des onglets concernés :
  //  - 'historique' : affichage des factures émises (shape aminci).
  //  - 'facturation' : la garde anti-double-facturation (findBlockingCommission)
  //    + le verrou ajustements ont besoin des lignes BRUTES (invoicesRaw).
  //  - 'compta' : même besoin de cohérence du verrou ajustements.
  //  - 'donnees' : le badge « factures en attente » lit invoicesRaw (BUG5).
  //  - 'logements' : l'impact de suppression compte les factures par logement_id (BUG5).
  useEffect(() => {
    if (tab === 'donnees' || tab === 'historique' || tab === 'facturation' || tab === 'compta' || tab === 'logements') refreshInvoices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  // Pass B « Recâblage compteur Paramètres » — wrappers fetch Bearer session
  // (calque callDeleteInvoice). getInvoiceSequences (lecture) + setInvoiceSequence
  // (écriture avance-seulement). Passés à ParamsPanel (section Compteurs factures).
  const callGetInvoiceSequences = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch('/api/beds24-mapping?action=getInvoiceSequences', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token || ''}`,
      },
      body: JSON.stringify({ action: 'getInvoiceSequences' }),
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(json.error || `http_${res.status}`)
    return json.sequences || []
  }

  const callSetInvoiceSequence = async ({ prefix, year, last_number }) => {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch('/api/beds24-mapping?action=setInvoiceSequence', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token || ''}`,
      },
      body: JSON.stringify({ action: 'setInvoiceSequence', prefix, year, last_number }),
    })
    const json = await res.json().catch(() => ({}))
    return { ok: res.ok, status: res.status, ...json }
  }

  // DETTE 9 — nettoyage contrats orphelins (bucket privé `contracts`). Wrappers
  // fetch Bearer session (calque callGetInvoiceSequences). list (lecture) +
  // delete (suppression, gardée serveur par re-check DBR). Passés à ParamsPanel.
  const callContractsOrphansList = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch('/api/beds24-mapping?action=contracts_orphans_list', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token || ''}`,
      },
      body: JSON.stringify({ action: 'contracts_orphans_list' }),
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(json.error || `http_${res.status}`)
    return json.orphans || []
  }

  const callContractsOrphansDelete = async (requestId) => {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch('/api/beds24-mapping?action=contracts_orphans_delete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token || ''}`,
      },
      body: JSON.stringify({ action: 'contracts_orphans_delete', request_id: requestId }),
    })
    const json = await res.json().catch(() => ({}))
    return { ok: res.ok, status: res.status, ...json }
  }

  // BUG 3 (aperçu commission) — pré-charge les séquences AU MOUNT (lecture seule)
  // pour que l'aperçu commission affiche le numéro PRÉVU sans rendre le handler
  // asynchrone (popup-blocker). Réutilise callGetInvoiceSequences (PAS de fetch
  // dupliqué). Échec silencieux : l'aperçu retombe sur le fallback `{prefix}-XXXX`.
  useEffect(() => {
    // getInvoiceSequences est requireAdmin — seul l'admin a un usage de l'aperçu
    // commission. Le ménage et le proprietaire (espace dédié) ne déclenchent pas
    // cet appel (évite un 403 inutile au mount).
    if (currentUser?.role !== 'admin') return
    let mounted = true
    callGetInvoiceSequences()
      .then(seqs => { if (mounted) setInvoiceSequences(seqs || []) })
      .catch(e => { if (mounted) console.warn('[App/preloadInvoiceSequences] échec:', e?.message) })
    return () => { mounted = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role])

  // BUG 3 (aperçu commission) — Aperçu LECTURE SEULE de la facture commission
  // mensuelle proprio AVANT émission. 100% SYNCHRONE (aucun await) : numéro PRÉVU
  // calculé depuis invoiceSequences déjà en state, window.open immédiat (anti
  // popup-blocker). N'appelle NI nextInvoiceNumberV2 NI createInvoiceV2 NI saveLog
  // → ne consomme AUCUN numéro CGI 289, ne crée AUCUNE invoice, n'envoie RIEN.
  const handlePreviewFacture = (prop, log) => {
    // F13 : données de facturation non lues → aucun aperçu calculé sur un vide.
    if (argentLectureRatee) { console.warn('[facture] aperçu refusé : données de facturation non lues'); return }
    // Préfixe série commission FBZ ('P') — JAMAIS le préfixe résa/voyageur.
    const prefix = resolveInvoicePrefixV2({ docType: 'commission_proprio' })
    const year = new Date().getFullYear()
    // Numéro PRÉVU (lecture seule) ou fallback non-null. JAMAIS consommé en DB.
    const previewNumber = computePreviewNumber({ prefix, year, sequences: invoiceSequences }) || `${prefix}-XXXX`
    // genFactureHTMLPure DIRECT (pas le wrapper positionnel genFactureHTML) pour
    // passer previewNumber. Mêmes dépendances que les call-sites émetteurs.
    // Paquet argent 2026-09-03 : la génération REFUSE désormais un montant
    // incalculable / un taux manquant (throw) → on affiche le message au lieu
    // de laisser l'erreur muette dans la console.
    let html
    try {
      ({ html } = genFactureHTMLPure({
        prop, log, cfg, data, incidents, month, monthLabel, y, m,
        mode: 'preview', invoice_number: null, previewNumber,
      }))
    } catch (e) {
      alert('⛔ ' + (e?.message || e))
      return
    }
    // inflateFactureSnapshot OBLIGATOIRE : injecte le logo réel (__LOGO_B64__).
    // Faille N1 : document ouvert dans un cadre isolé (src/lib/apercu-isole.js).
    if (!ouvrirApercuIsole(inflateFactureSnapshot(html))) { alert('Popup bloqué — autorise les popups pour ce site.'); return }
  }

  // Chantier D2 — reçoit l'ENTRÉE (pas l'index) : dé-positionnalisation. Le flow
  // purge le KV par identité (invoiceId / fallback num) via updater functional,
  // robuste à tout réordonnancement du factLog entre modale et confirmation.
  // Brouillon seulement : une pièce émise est refusée (flow ET serveur).
  const deleteFactureCoherent = (facture) =>
    deleteFactureFlow(facture, {
      callDeleteInvoice,
      saveLog,
      onError: (msg) => alert(msg),
      done: () => { setConfirm(null); refreshInvoices() },
    })

  // ── AVOIR (chantier « L'avoir a un bouton ») ────────────────────────────
  // Action serveur emitCreditNote (requireAdmin, service_role → RPC
  // emit_credit_note, migration 20270114000001). Pièce NEUVE série AV-* liée à
  // l'originale ; l'originale reste (statut « annulée »), rien n'est effacé.
  // Le numéro annoncé à l'écran voyage comme confirmOriginalNumber : le
  // serveur le compare à la base (liste périmée ⇒ 409, rien d'écrit).
  const callEmitCreditNote = async ({ invoiceId, html_snapshot, confirmOriginalNumber, meta }) => {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch('/api/beds24-mapping?action=emitCreditNote', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token || ''}`,
      },
      body: JSON.stringify({ action: 'emitCreditNote', invoiceId, html_snapshot, confirmOriginalNumber, meta }),
    })
    const json = await res.json().catch(() => ({}))
    return { ...json, ok: res.ok, message: json.message || json.error || `Avoir refusé (http_${res.status}).` }
  }
  // Verrou synchrone : MÊME fenêtre double-clic que sendFactureEmail et
  // sendAvoir. Deux clics rapides émettraient DEUX avoirs — le second serait
  // refusé par la RPC (invoice_already_cancelled), mais seulement APRÈS avoir
  // consommé un numéro de la série AV-* : un trou dans une séquence fiscale,
  // que rien ne peut ensuite reboucher (CGI 289).
  const emitAvoir = async (facture) => {
    // P-2.46 : avoir d'une pièce de la conciergerie et « Assujetti TVA » coché
    // ⇒ refus avant l'appel (aucun numéro AV). Le serveur porte le même garde.
    const refusTvaAvoir = estSerieConciergerie(facture) && refusEmissionTva(cfg, { emetteur: 'fbz' })
    if (refusTvaAvoir) { alert('⛔ ' + refusTvaAvoir.message); setConfirm(null); setConfirmAvoirChecked(false); return }
    if (factureOpLock.current) return
    factureOpLock.current = true
    try {
      await emitAvoirFlow(facture, {
        callEmitCreditNote,
        buildHtml: (entry) => genAvoirHTML({ original: entry, cfg }),
        onError: (msg) => alert(msg),
        onSuccess: (r) => {
          alert(`✅ Avoir ${r.credit_number} émis. La facture ${r.original_number} est annulée — elle reste archivée (section « Annulées et avoirs » de l'Historique). Le mois redevient facturable.`)
          // Les compteurs ont bougé (série AV-*) : on les relit pour le prochain aperçu.
          signalerNumeroEmis()
          callGetInvoiceSequences().then(seqs => setInvoiceSequences(seqs || [])).catch(e => console.warn('[emitAvoir/sequences]', e?.message))
        },
        done: () => { setConfirm(null); setConfirmAvoirChecked(false); refreshInvoices() },
      })
    } finally {
      factureOpLock.current = false
    }
  }

  // ── ENVOI DE L'AVOIR au propriétaire (chantier « L'avoir part chez le
  // propriétaire », décision Dimitri). MÊME CHEMIN qu'une facture :
  // sendThenMarkDelivered (envoi EmailJS, PUIS setInvoiceDelivered sur l'AVOIR,
  // seulement si l'envoi a réussi) — via src/lib/avoir-send-flow.js. Le CONTENU
  // diffère : le corps nomme la pièce annulée, son numéro, son montant, et ce
  // qui la remplace. L'ORIGINALE N'EST JAMAIS ÉCRITE par ce geste : elle est
  // `cancelled`, et « le propriétaire a reçu l'explication » se lit sur
  // delivered_at de l'avoir (related_invoice_id dit laquelle).
  //
  // Destinataire : meta.email_dest de l'avoir (copié de l'originale à
  // l'émission), sinon l'e-mail COURANT du propriétaire du logement (cfg).
  //
  // ⚠️ Le propriétaire est résolu AUJOURD'HUI dans cfg, alors que l'avoir porte
  // le nom FIGÉ à l'émission (meta.prop_nom). Si la fiche a changé de mains
  // entre-temps, l'adresse trouvée ici est celle du NOUVEAU propriétaire et la
  // pièce concerne l'ancien : describeAvoirEnvoi refuse le geste (propActuel).
  const resolveAvoirProprio = (avoir) =>
    (cfg?.props || []).find(p => (p.logs || []).some(l => l.id === avoir?.logId || (l.chambres || []).some(c => c.id === avoir?.logId))) || null
  const resolveAvoirEmailDest = (avoir) => {
    if (avoir?.emailDest) return avoir.emailDest
    return resolveAvoirProprio(avoir)?.email || null
  }
  // Facture ACTIVE qui remplace l'originale pour la même période et le même
  // logement, si elle existe (numérotée, même série). Lecture seule.
  const findAvoirRemplacante = (avoir) =>
    (invoicesList || []).find(f => f.kind === 'invoice' && !isDraftInvoice(f) && f.logId && f.logId === avoir?.logId && f.mois === avoir?.mois && f.invoiceId !== avoir?.relatedInvoiceId) || null
  const sendAvoir = async (avoir) => {
    // Verrou synchrone : même fenêtre double-clic que sendFactureEmail.
    if (factureOpLock.current) return
    factureOpLock.current = true
    const originale = (invoicesArchiveList || []).find(f => f.invoiceId === avoir?.relatedInvoiceId) || null
    const emailDest = resolveAvoirEmailDest(avoir)
    const remplacante = findAvoirRemplacante(avoir)
    try {
      await sendAvoirFlow(avoir, {
        emailDest, originale, remplacante,
        propActuel: resolveAvoirProprio(avoir)?.nom || null,
        loadAvoirHtml: (id) => loadSnapshotForEmail(id),
        send: async (params, template) => { await ensureEmailJs(); return window.emailjs.send(EMAILJS_SERVICE, template, params) },
        markDelivered: (id) => callSetInvoiceDelivered(id, true),
        onError: (msg) => alert(msg),
        onSuccess: (r) => {
          if (r.delivered) {
            alert(r.resent
              ? `✅ Avoir ${avoir.num} renvoyé à ${r.emailDest}. Sa date de première transmission est conservée. La facture ${r.originalNumber} reste chez le propriétaire, inchangée.`
              : `✅ Avoir ${avoir.num} envoyé à ${r.emailDest} et marqué transmis. La facture ${r.originalNumber} reste chez le propriétaire, inchangée — l'avoir lui en explique l'annulation.`)
          } else {
            // Le mail EST parti : on ne laisse pas l'écran dire « non transmis ».
            console.error('[sendAvoir] mail parti, marquage transmis refusé :', r.message)
            alert(`⚠️ Avoir ${avoir.num} envoyé à ${r.emailDest}, mais la date de transmission n'a PAS pu être enregistrée (${r.message || 'erreur'}).`)
          }
        },
        done: () => { setConfirm(null); setConfirmEnvoiAvoirChecked(false); refreshInvoices() },
      })
    } finally {
      factureOpLock.current = false
    }
  }

  // openFactureSnapshot vit désormais dans src/lib/facture-snapshot.js (importé
  // en tête). Il en existait DEUX copies identiques — ici et dans
  // HistoriquePanel — et seule celle d'HistoriquePanel avait reçu la
  // substitution des jetons de brouillon : le bouton « Voir la facture » de
  // l'onglet Facturation servait un document affichant `N° __INVOICE_NUMBER__`.
  // Une seule implémentation désormais : plus rien à oublier des deux côtés.

  return {
    emettreBrouillon, callGetInvoiceSequences, callSetInvoiceSequence, callContractsOrphansList,
    callContractsOrphansDelete, handlePreviewFacture, deleteFactureCoherent, emitAvoir,
    resolveAvoirProprio, resolveAvoirEmailDest, findAvoirRemplacante, sendAvoir,
  }
}
